import {createSelector} from 'reselect'
import {isPTOEnabled, isSDBEnabled, isWASEnabled, isBTYEnabled, isURAEnabled} from '../../../features/selectors'
import {UserManagementConstants} from "constants";
import {getEditUserId} from "../../../panels/user/editPanel/selectors";
import {getUserId as getWorkAssignmentPanelUserId} from "../workAssignmentPanel/selectors";
import {getUserId as getManagementAssignmentsPanelUserId} from "../editManagementAssignmentsPanel/selectors";
import {haveMoreLicenses, isLicenseViolated} from "./utils"
import {getUserId as getTeamAssignmentsUserId} from "../../../panels/user/editTeamAssignmentsPanel/selectors";
import {arrayToSet} from '../../../atUtils'
import {getLoggedInUser} from '../../../loggedInUser/selectors'
import {userComparator} from '../../../usersSelector/user_group_selector_util'
import {createDeepEqualSelector} from '../../../redux/utils'

export const getCommonState = (state) => state.userManagement.common

export const getViewerSettings = createSelector(
    getCommonState,
    (commonState) => commonState.viewerSettings
)

export const isPTOTabVisible = createSelector(
    getViewerSettings,
    isPTOEnabled,
    (viewerSettings, ptoEnabled) => viewerSettings.allowedToManageLeaveBalanceSettings && ptoEnabled
)

export const isSDBTabVisible = createSelector(
    getViewerSettings,
    isSDBEnabled,
    (viewerSettings, sdbEnabled) => viewerSettings.allowedToManageLeaveBalanceSettings && sdbEnabled
)

export const isWASTabVisible = createSelector(
    getViewerSettings,
    isWASEnabled,
    (viewerSettings, wasEnabled) => viewerSettings.allowedToManageScope  &&
                                    (viewerSettings.allowedToManageAccountSettings || viewerSettings.hasAccessToScopeOfWork && viewerSettings.hasAccessToUsers) &&
                                    wasEnabled
)

export const isAccountSettingsTabVisible = createSelector(
    getViewerSettings,
    (viewerSettings) => viewerSettings.showAccountSettings
)

export const getActiveTabNum = createSelector(
    getCommonState,
    isAccountSettingsTabVisible,
    isPTOTabVisible,
    isSDBTabVisible,
    isWASTabVisible,
    (commonState, showAccSettings, showPTO, showSDB, showWAS) => {
        let tabNum = commonState.activeTab
        if (!showPTO && tabNum === UserManagementConstants.PTO_SETTINGS_TAB_NUM) {
            tabNum = UserManagementConstants.ACCOUNT_SETTINGS_TAB_NUM
        }
        if (!showSDB && tabNum === UserManagementConstants.SICK_DAYS_SETTINGS_TAB_NUM) {
            tabNum = UserManagementConstants.ACCOUNT_SETTINGS_TAB_NUM
        }
        if (!showWAS && tabNum === UserManagementConstants.WORK_ASSIGNMENTS_TAB_NUM) {
            tabNum = UserManagementConstants.ACCOUNT_SETTINGS_TAB_NUM
        }
        if (!showAccSettings && tabNum === UserManagementConstants.ACCOUNT_SETTINGS_TAB_NUM) {
            if (showPTO)
                tabNum = UserManagementConstants.PTO_SETTINGS_TAB_NUM
            else if (showSDB)
                tabNum = UserManagementConstants.SICK_DAYS_SETTINGS_TAB_NUM
            else
                tabNum = UserManagementConstants.WORK_ASSIGNMENTS_TAB_NUM
        }

        return tabNum
    }
)

export const getUsersWithoutApprovers = createSelector(
    getCommonState,
    (commonState) => commonState.usersWithoutApprovers
)

export const getLicenseCounters = createSelector(
    getCommonState,
    (commonState) => commonState.licenseCounters
)

export const isAllowedToManageAccountSettings = createSelector(
    getViewerSettings,
    (viewerSettings) => viewerSettings.allowedToManageAccountSettings
)

export const isPossibleToCreateUsers = createSelector(
    isAllowedToManageAccountSettings,
    getLicenseCounters,
    (allowedToManageAccountSettings, licenseCounters) => allowedToManageAccountSettings
                                                         && !isLicenseViolated(licenseCounters)
                                                         && haveMoreLicenses(licenseCounters)
)

export const licenseViolated = createSelector(
    getLicenseCounters,
    (licenseCounters) => isLicenseViolated(licenseCounters)
)

export const getNewUsersIdSet = createSelector(
    getCommonState,
    (commonState) => commonState.newUsersIdSet
)

export const getViewerManagementCounters = (state) => getCommonState(state).viewerManagementAssignmentsCounters

export const getAssignedUserIds = createSelector(
    getViewerSettings,
    (viewer) => viewer.assignedUserIds
)

export const getEditingUserId = createSelector(
    getEditUserId,
    getWorkAssignmentPanelUserId,
    getManagementAssignmentsPanelUserId,
    getTeamAssignmentsUserId,
    (editPanelUserId, workAssignmentsPanelUserId, managementAssignmentsPanelUserId, teamAssignmentsUserId) => editPanelUserId || workAssignmentsPanelUserId || managementAssignmentsPanelUserId || teamAssignmentsUserId
)

export const isGenerateMoneyReportsEnabled = createSelector(
    isURAEnabled,
    isBTYEnabled,
    (URAEnabled, BTYEnabled) => URAEnabled || BTYEnabled
)

export const getUsersList = createSelector(
    getCommonState,
    (commonState) => commonState.usersList
)

export const getSortedUsersList = createDeepEqualSelector(
    getUsersList,
    (usersListMap) => Object.values(usersListMap).sort(userComparator)
)

export const getActiveAssignedUserIds = createSelector(
    getUsersList,
    getAssignedUserIds,
    (usersList, assignedUserIds) => {
        return assignedUserIds !== null
            ? assignedUserIds.filter((id) => usersList[id].activeInAT || usersList[id].enabled)
            : null
    }
)

export const getUsersListForWorkAssignmentsTab = createSelector(
    getSortedUsersList,
    getAssignedUserIds,
    getLoggedInUser,
    (usersList, assignedUserIds, loggedInUser) => {
        if (assignedUserIds == null) {
            return usersList.filter((user) => {
                return (user.activeInAT || user.enabled) && user.id !== loggedInUser.id
            })
        } else {
            const assignedIdsSet = arrayToSet(assignedUserIds)
            return usersList.filter((user) => {
                return !!assignedIdsSet[user.id] && (user.activeInAT || user.enabled) && user.id !== loggedInUser.id
            })
        }
    }
)

export const getUserManagementData = createSelector(
    getCommonState,
    (commonState) => commonState.userManagementData
)

export const getPermissionsTemplates = createSelector(
    getUserManagementData,
    (userManagementData) => userManagementData.permissionsTemplates
)

export const getDefaultWorkSchedule = state => getUserManagementData(state).defaultWorkSchedule


// WEBPACK FOOTER //
// ./web/js/administration/userManagement/common/selectors.js